import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth } from '../configs/firebaseConfig';
import {
  identifySignedInUser,
  resetAnalytics,
  trackEvent,
  trackMixpanelEvent,
  getSafeAuthFailureProperties,
  getAnalyticsUserState,
  identifyPermanentUserIfReady,
} from '../utils/Analytics';
import { useAnalyticsConsent } from './AnalyticsConsentContext';
import { serverTimestamp } from 'firebase/firestore';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../configs/firebaseConfig';
import { waitForAuthInitialization } from '../utils/auth/authState';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { analyticsReady } = useAnalyticsConsent();

  const popupCancellationCodes = new Set([
    'auth/popup-blocked',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/internal-error',
  ]);

  async function ensureUserInFirestore(user) {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        email: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        originProduct: "lettucetalk",
      });
    }
  }

  // Helper for tracking auth events
  function trackAuthEvent(action, user, method) {
    trackEvent('User', action, `User ${action.toLowerCase()}`);
  }

  // Sign up function
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password).then((userCredential) => {
      const user = userCredential.user;
      trackAuthEvent('Sign Up', user, 'email');
      return userCredential;
    });
  }

  // Login function 
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password).then((userCredential) => {
      const user = userCredential.user;
      trackAuthEvent('Sign In', user, 'email');
      return userCredential;
    });
  }

  // Logout function
  function logout() {
    if (currentUser) {
      trackAuthEvent('Sign Out', currentUser, currentUser.providerData?.[0]?.providerId || 'unknown');
    }
    return signOut(auth).then(() => {
      resetAnalytics();
    });
  }

  // Google sign-in
  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();

    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      const isNewUser = userCredential.additionalUserInfo?.isNewUser;

      trackAuthEvent(isNewUser ? 'Sign Up' : 'Sign In', user, 'google');
      identifySignedInUser(user.uid);
      trackMixpanelEvent("User Signed In", {
        auth_provider: "google",
        auth_flow: isNewUser ? "sign_up" : "sign_in",
      });

      // TODO: if (isNewUser), then do below?
      await ensureUserInFirestore(user);

      return userCredential;

    } catch (error) {
      if (!popupCancellationCodes.has(error?.code)) {
        trackMixpanelEvent(
          "Authentication Failed",
          getSafeAuthFailureProperties({
            authProvider: "google",
            authStep: "popup",
            errorCode: error?.code,
            userState: getAnalyticsUserState(auth.currentUser),
          })
        );
      }
      if (
        error.code === 'auth/popup-blocked' ||
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/internal-error' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        alert("Sign-in didn't work. Please try again in a different browser or device.");
      } else {
        console.error("Unhandled Google Sign-In error:", error);
      }

      return Promise.reject(error);
    }
  }


  // Password reset
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Update profile
  function updateUserProfile(user, data) {
    return updateProfile(user, data);
  }

  // Set up auth state listener
  useEffect(() => {
    let isMounted = true;
    let unsubscribe = () => {};

    waitForAuthInitialization().then(() => {
      if (!isMounted) return;

      unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setLoading(false);
      });
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    identifyPermanentUserIfReady(currentUser, analyticsReady);
  }, [analyticsReady, currentUser]);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    signInWithGoogle,
    resetPassword,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
